<template>
  <div class="tab-bar-container">
    <el-tabs
      v-model="activeTab"
      class="custom-tabs"
      @tab-click="handleTabClick"
      @tab-remove="handleTabRemove"
    >
      <el-tab-pane
        v-for="tab in visitedViews"
        :key="tab.path"
        :label="tab.title"
        :name="tab.path"
        :closable="tab.path !== '/dashboard'"
      >
        <template #label>
          <el-icon v-if="tab.icon" class="tab-icon">
            <component :is="tab.icon" />
          </el-icon>
          <span>{{ tab.title }}</span>
        </template>
      </el-tab-pane>
    </el-tabs>
    
    <div class="tab-actions">
      <el-dropdown trigger="click" @command="handleCommand">
        <el-button type="primary" link>
          <el-icon><More /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="closeOthers">关闭其他标签</el-dropdown-item>
            <el-dropdown-item command="closeAll">关闭所有标签</el-dropdown-item>
            <el-dropdown-item command="refresh">刷新当前页面</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { 
  More, 
  DataLine, 
  List, 
  Delete, 
  Message,
  Umbrella,
  CreditCard,
  Document,
  Cpu,
  Setting
} from '@element-plus/icons-vue'

const router = useRouter()
const route = useRoute()

// 访问过的视图
const visitedViews = ref([
  { 
    path: '/dashboard', 
    title: '数据概况', 
    icon: 'DataLine',
    meta: { title: '数据概况', icon: 'DataLine' }
  }
])

// 当前激活的标签
const activeTab = computed(() => route.path)

// 添加视图
const addView = (route) => {
  // 排除登录页面
  if (route.path === '/login') return
  
  // 如果没有标题，从menuMap中获取
  const menuMap = {
    '/dashboard': { title: '数据概况', icon: 'DataLine' },
    '/auth/list': { title: '授权列表', icon: 'List' },
    '/auth/recycle': { title: '回收站', icon: 'Delete' },
    '/service/sms': { title: '短信服务', icon: 'Message' },
    '/service/insurance': { title: '保险服务', icon: 'Umbrella' },
    '/service/payment': { title: '支付设置', icon: 'CreditCard' },
    '/code/frontend': { title: '前端代码', icon: 'Document' },
    '/code/backend': { title: '后端代码', icon: 'Cpu' },
    '/settings/site': { title: '站点设置', icon: 'Setting' }
  }

  const meta = menuMap[route.path] || route.meta
  if (!meta?.title) return

  const isExists = visitedViews.value.some(v => v.path === route.path)
  if (!isExists) {
    visitedViews.value.push({
      path: route.path,
      title: meta.title,
      icon: meta.icon,
      meta: meta
    })
  }
}

// 监听路由变化，添加标签页
watch(
  () => route.path,
  (newPath) => {
    if (newPath === '/login') {
      // 清空标签页，只保留首页
      visitedViews.value = visitedViews.value.filter(tab => tab.path === '/dashboard')
    } else {
      addView(route)
    }
  },
  { immediate: true }
)

// 处理标签点击
const handleTabClick = (tab) => {
  if (tab.props.name === activeTab.value) {
    return
  }
  router.push(tab.props.name)
}

// 处理标签关闭
const handleTabRemove = (targetPath) => {
  if (targetPath === '/dashboard') {
    return
  }

  const tabs = visitedViews.value
  let activePath = activeTab.value
  
  if (activePath === targetPath) {
    tabs.forEach((tab, index) => {
      if (tab.path === targetPath) {
        const nextTab = tabs[index + 1] || tabs[index - 1]
        if (nextTab) {
          activePath = nextTab.path
        }
      }
    })
  }
  
  visitedViews.value = tabs.filter(tab => tab.path !== targetPath)
  if (activePath && activePath !== targetPath) {
    router.push(activePath)
  }
}

// 处理下拉菜单命令
const handleCommand = (command) => {
  switch (command) {
    case 'closeOthers':
      visitedViews.value = visitedViews.value.filter(
        tab => tab.path === activeTab.value || tab.path === '/dashboard'
      )
      break
      
    case 'closeAll':
      visitedViews.value = visitedViews.value.filter(
        tab => tab.path === '/dashboard'
      )
      router.push('/dashboard')
      break
      
    case 'refresh':
      // 直接刷新当前页面
      window.location.reload()
      break
  }
}
</script>

<style scoped lang="scss">
.tab-bar-container {
  position: relative;
  padding: 6px 16px;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.custom-tabs {
  flex: 1;
  
  :deep(.el-tabs__header) {
    margin: 0;
    border-bottom: none;
    
    .el-tabs__nav-wrap::after {
      display: none;
    }
    
    .el-tabs__nav {
      border: none;
      border-radius: 4px;
    }
    
    .el-tabs__item {
      height: 32px;
      line-height: 32px;
      font-size: 14px;
      color: #606266;
      border: none;
      padding: 0 16px;
      border-radius: 4px;
      transition: all 0.3s;
      margin: 0 2px;
      position: relative;
      
      .tab-icon {
        margin-right: 6px;
        font-size: 14px;
      }
      
      &:hover {
        color: var(--el-color-primary);
        background-color: var(--el-color-primary-light-9);
      }
      
      &.is-active {
        color: var(--el-color-primary);
        background-color: var(--el-color-primary-light-9);
        border: none;
        font-weight: 500;
      }
    }
  }
}

.tab-actions {
  margin-left: 16px;
  
  .el-button {
    padding: 8px;
    border-radius: 4px;
    
    &:hover {
      background-color: var(--el-color-primary-light-9);
    }
    
    .el-icon {
      font-size: 16px;
    }
  }
}

:deep(.el-tabs__nav) {
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 6px;
  background-color: #f6f8fa;
}

:deep(.el-tabs__item) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .tab-icon {
    margin-right: 6px;
    transition: all 0.3s;
  }
  
  .el-icon-close {
    margin-left: 4px;
    border-radius: 50%;
    font-size: 12px;
    height: 14px;
    width: 14px;
    text-align: center;
    line-height: 14px;
    vertical-align: middle;
    top: -1px;
    right: 0;
    transform-origin: 100% 50%;
    transition: all 0.3s;
    opacity: 0;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
      color: #606266;
    }
  }
  
  &:hover {
    .el-icon-close {
      opacity: 1;
    }
  }
  
  &.is-active {
    .tab-icon {
      transform: scale(1.1);
    }
    
    .el-icon-close {
      opacity: 1;
      &:hover {
        background-color: rgba(64, 158, 255, 0.1);
        color: var(--el-color-primary);
      }
    }
  }
}

:deep(.el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  
  &:hover {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
  }
  
  .el-icon {
    margin-right: 8px;
    font-size: 16px;
  }
}
</style>